import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";
import FaqItem from "../components/ui/FaqItem";
import { cn } from "../lib/utils";

/* ------------------------------------------------------------------ */
/*  Mock content                                                       */
/* ------------------------------------------------------------------ */

type Period = "monthly" | "annual";

type Feature = string | { text: string; hint: string };

type Plan = {
  name: string;
  nameStyle: React.CSSProperties;
  monthly: string;
  annual: string;
  savings: string;
  desc: string;
  meta: string;
  metaStyle?: React.CSSProperties;
  plusBanner?: { text: string; tone: "teal" | "blue" };
  coreCategoryLabel?: string;
  coreFeatures: Feature[];
  support: string[];
  cta: { label: string; to: string; variant: "btn-ghost" | "btn-accent" };
  featured?: boolean;
};

const PLANS: Plan[] = [
  {
    name: "STARTER",
    nameStyle: {
      color: "var(--teal)",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
    monthly: "₹6,000",
    annual: "₹5,100",
    savings: "Billed ₹61,200/yr · save ₹10,800",
    desc: "For small breweries and distributors ready to move off spreadsheets and start running operations properly.",
    meta: "2 users included · ₹500/extra user/mo",
    metaStyle: { color: "var(--text-muted)" },
    coreFeatures: [
      "Core onboarding",
      { text: "Order taking & basic invoicing", hint: "(up to 200/mo)" },
      "Buyer ordering portal",
      "Live inventory dashboard & stock alerts",
      "Basic payment tracking",
      "Order & customer analytics",
    ],
    support: [
      "First 2 months free support",
      "5 support queries / year",
      "Email support",
    ],
    cta: { label: "Get started", to: "/signup", variant: "btn-ghost" },
  },
  {
    name: "GROWTH",
    nameStyle: {
      color: "rgba(147,197,253,0.8)",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
    monthly: "₹25,000",
    annual: "₹21,250",
    savings: "Billed ₹2,55,000/yr · save ₹45,000",
    desc: "For mid-size businesses scaling operations across multiple reps and buyer accounts.",
    meta: "5 users included · ₹1,000/extra user/mo",
    metaStyle: { color: "rgba(147,197,253,0.7)" },
    plusBanner: { text: "Everything in Starter, plus:", tone: "blue" },
    coreCategoryLabel: "WHAT'S NEW IN GROWTH",
    coreFeatures: [
      { text: "Up to 800 orders / month", hint: "(4\u00d7 Starter)" },
      "Bulk order templates",
      "Multi-location inventory management",
      "Full payment tracking & auto-reconciliation",
      "Credit cycle management",
      "Sales rep dashboards & performance analytics",
    ],
    support: [
      "First 2 months free support",
      "15 support queries / year",
      "Priority Support & Onboarding",
    ],
    cta: { label: "Get started", to: "/signup", variant: "btn-accent" },
    featured: true,
  },
  {
    name: "ENTERPRISE",
    nameStyle: {
      color: "var(--teal)",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
    monthly: "₹45,000",
    annual: "₹38,250",
    savings: "Billed ₹4,59,000/yr · save ₹81,000",
    desc: "For large distributors and chains with complex workflows, high volumes, and multi-location operations.",
    meta: "10 users included · ₹2,000/extra user/mo",
    metaStyle: { color: "var(--text-muted)" },
    plusBanner: { text: "Everything in Growth, plus:", tone: "teal" },
    coreCategoryLabel: "WHAT'S NEW IN ENTERPRISE",
    coreFeatures: [
      "Unlimited order volume",
      "Custom API & third-party integrations",
      "Dedicated account manager",
      "On-site onboarding",
    ],
    support: [
      "SLA guarantee",
      "Unlimited support queries",
      "White-glove account management",
    ],
    cta: { label: "Get in touch", to: "/contact", variant: "btn-ghost" },
  },
];

const ADDONS: Array<{ name: string; price: string }> = [
  {
    name: "Extra User / Rep",
    price: "₹500 (Starter) · ₹1,000 (Growth) · ₹2,000 (Enterprise) per user/mo",
  },
  {
    name: "Onboarding Support & Custom Integrations",
    price: "₹50,000 one-time + ₹2,500 / hr",
  },
  { name: "Enterprise SLA Upgrade", price: "Custom quote" },
  {
    name: "Support Add-on",
    price: "₹1,000 / mo · includes 10 additional queries",
  },
];

type CompareCell = string | "yes" | "no";
type CompareRow = {
  label: string;
  values: [CompareCell, CompareCell, CompareCell];
};
type CompareSection = { title: string; rows: CompareRow[] };

const COMPARE: CompareSection[] = [
  {
    title: "ORDER MANAGEMENT",
    rows: [
      {
        label: "Monthly order volume",
        values: ["Up to 200", "Up to 800", "Unlimited"],
      },
      { label: "Buyer ordering portal", values: ["yes", "yes", "yes"] },
      { label: "Bulk order templates", values: ["no", "yes", "yes"] },
      { label: "Real-time order status", values: ["yes", "yes", "yes"] },
    ],
  },
  {
    title: "INVENTORY",
    rows: [
      { label: "Live inventory dashboard", values: ["yes", "yes", "yes"] },
      { label: "Stock-out alerts", values: ["yes", "yes", "yes"] },
      { label: "Multi-location inventory", values: ["no", "yes", "yes"] },
    ],
  },
  {
    title: "FINANCE & CREDIT",
    rows: [
      { label: "Payment tracking", values: ["Basic", "Full", "Full"] },
      { label: "Credit cycle management", values: ["no", "yes", "yes"] },
      { label: "Auto-reconciliation", values: ["no", "yes", "yes"] },
      { label: "Custom API & integrations", values: ["no", "no", "yes"] },
    ],
  },
  {
    title: "SALES & REPS",
    rows: [
      { label: "Sales rep dashboard", values: ["no", "yes", "yes"] },
      { label: "Rep performance analytics", values: ["no", "yes", "yes"] },
    ],
  },
  {
    title: "SUPPORT",
    rows: [
      { label: "Email support", values: ["yes", "yes", "yes"] },
      { label: "Priority support", values: ["no", "yes", "yes"] },
      { label: "Dedicated account manager", values: ["no", "no", "yes"] },
      { label: "On-site onboarding", values: ["no", "no", "yes"] },
      { label: "SLA guarantee", values: ["no", "no", "yes"] },
    ],
  },
];

const FAQS: Array<{ q: string; a: string }> = [
  {
    q: "Can I switch plans later?",
    a: "Yes, you can upgrade or downgrade at any time. When upgrading, you'll get immediate access to the new plan's features and we'll prorate the billing. Downgrading takes effect at the start of your next billing cycle.",
  },
  {
    q: "What happens if I exceed my order limit?",
    a: "We'll notify you when you're approaching your limit. You won't be cut off mid-month. Orders will continue to process, and we'll recommend an upgrade if your usage consistently exceeds your plan.",
  },
  {
    q: "How long does onboarding take?",
    a: "Most teams are fully operational within 48 hours. We handle data migration, configure your account, and provide a guided walkthrough. Enterprise customers get a dedicated onboarding manager.",
  },
  {
    q: "Is there a free trial?",
    a: "We offer a 14-day free trial on all plans. No credit card required. You'll get full access to every feature in your chosen plan so you can evaluate it with real data.",
  },
  {
    q: "Do you support multi-currency?",
    a: "Growth and Enterprise plans include full multi-currency support. You can set base currencies per warehouse, manage exchange rates, and generate reports in any supported currency.",
  },
  {
    q: "Can Maltora replace our ERP?",
    a: "Maltora integrates with your existing ERP rather than replacing it. For Enterprise customers, we offer deep two-way sync with popular systems like SAP, Oracle, and Tally. Many smaller operations find Maltora handles everything they need without a separate ERP.",
  },
];

/* ------------------------------------------------------------------ */
/*  Inline-style snippets re-used by the comparison table              */
/* ------------------------------------------------------------------ */

const cellBase: React.CSSProperties = {
  padding: "14px 20px",
  borderBottom: "1px solid rgba(15,23,42,0.05)",
  fontSize: 14,
};
const cellCenter: React.CSSProperties = { ...cellBase, textAlign: "center" };
const cellGrowth: React.CSSProperties = {
  ...cellCenter,
  background: "rgba(15,23,42,0.03)",
};
const cellMuted: React.CSSProperties = { color: "var(--text-muted)" };
const cellTeal: React.CSSProperties = { color: "var(--teal)" };

const headerStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "16px 20px",
  fontWeight: 600,
  borderBottom: "2px solid rgba(15,23,42,0.1)",
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
};
const headerCenter: React.CSSProperties = {
  ...headerStyle,
  textAlign: "center",
  width: "20%",
};
const headerGrowth: React.CSSProperties = {
  ...headerCenter,
  fontWeight: 700,
  background: "var(--bg-dark)",
  color: "var(--bg-base)",
};
const sectionLabelCell: React.CSSProperties = {
  padding: "24px 20px 8px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--teal)",
  borderBottom: "1px solid rgba(15,23,42,0.08)",
  background: "rgba(15,23,42,0.02)",
};

const renderCell = (val: CompareCell, isGrowthCol: boolean) => {
  const baseStyle = isGrowthCol ? cellGrowth : cellCenter;
  if (val === "yes")
    return <td style={{ ...baseStyle, ...cellTeal }}>&#10003;</td>;
  if (val === "no") return <td style={{ ...baseStyle, ...cellMuted }}>—</td>;
  return <td style={baseStyle}>{val}</td>;
};

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function PricingPage() {
  const [period, setPeriod] = useState<Period>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <div className="pricing-page">
        {/* Pricing hero */}
        <section
          className="section white pricing-hero"
          aria-labelledby="pricing-title"
        >
          <div className="container section-reveal">
            <div className="section-header centered pricing-hero__header">
              <span className="overline">PRICING</span>
              <h1 id="pricing-title">
                Simple. Transparent.
                <br />
                <em className="accent-italic">Built to grow</em> with you.
              </h1>
              <p>
                No hidden fees. No surprise charges. Start where you are and
                scale into what you need on your terms.
              </p>
            </div>

            <div className="pricing-toggle">
              <button
                className={cn("toggle-btn", period === "monthly" && "active")}
                onClick={() => setPeriod("monthly")}
                type="button"
              >
                Monthly
              </button>
              <button
                className={cn("toggle-btn", period === "annual" && "active")}
                onClick={() => setPeriod("annual")}
                type="button"
              >
                Annual <span className="toggle-save-badge">Save 15%</span>
              </button>
            </div>
          </div>
        </section>

        {/* Pricing cards */}
        <section
          className="section white pricing-plans"
          aria-label="Pricing plans"
        >
          <div className="container section-reveal">
            <div className="pricing-grid">
              {PLANS.map((plan) => (
                <div
                  key={plan.name}
                  className={cn("pricing-card", plan.featured && "featured")}
                >
                  {plan.featured && (
                    <div className="pricing-badge">MOST POPULAR</div>
                  )}
                  <div className="pricing-name" style={plan.nameStyle}>
                    {plan.name}
                  </div>
                  <div className="pricing-price">
                    <span className="pricing-amount">
                      {period === "monthly" ? plan.monthly : plan.annual}
                    </span>
                    <span className="pricing-period">/month</span>
                  </div>
                  <div
                    className={cn(
                      "pricing-savings",
                      period === "annual" && "is-visible"
                    )}
                  >
                    {plan.savings}
                  </div>
                  <div className="pricing-desc">{plan.desc}</div>
                  <div className="pricing-desc" style={plan.metaStyle}>
                    {plan.meta}
                  </div>

                  <div className="pricing-divider"></div>

                  {plan.plusBanner && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 16,
                        padding: "10px 12px",
                        background:
                          plan.plusBanner.tone === "blue"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(13,122,110,0.07)",
                        borderRadius: 8,
                        border:
                          plan.plusBanner.tone === "blue"
                            ? "1px solid rgba(147,197,253,0.15)"
                            : "1px solid rgba(13,122,110,0.15)",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={
                          plan.plusBanner.tone === "blue"
                            ? "rgba(147,197,253,0.7)"
                            : "var(--teal)"
                        }
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color:
                            plan.plusBanner.tone === "blue"
                              ? "rgba(147,197,253,0.75)"
                              : "var(--teal)",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {plan.plusBanner.text}
                      </span>
                    </div>
                  )}

                  <div className="pricing-category">
                    {plan.coreCategoryLabel ?? "CORE FEATURES"}
                  </div>
                  <div className="pricing-features">
                    {plan.coreFeatures.map((f) => {
                      const text = typeof f === "string" ? f : f.text;
                      const hint = typeof f === "string" ? null : f.hint;
                      return (
                        <div className="pricing-feature" key={text}>
                          <svg viewBox="0 0 24 24">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>
                            {text}
                            {hint && (
                              <span
                                style={{
                                  fontSize: "inherit",
                                  opacity: 0.6,
                                  marginLeft: 4,
                                }}
                              >
                                {hint}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="pricing-category">SUPPORT</div>
                  <div
                    className="pricing-features"
                    style={{ marginBottom: 32 }}
                  >
                    {plan.support.map((f) => (
                      <div className="pricing-feature" key={f}>
                        <svg viewBox="0 0 24 24">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {f}
                      </div>
                    ))}
                  </div>

                  {plan.cta.to.startsWith("#") ? (
                    <a
                      href={plan.cta.to}
                      className={cn("btn", plan.cta.variant)}
                      style={{ width: "100%", textAlign: "center" }}
                    >
                      {plan.cta.label}
                    </a>
                  ) : (
                    <Link
                      to={plan.cta.to}
                      className={cn("btn", plan.cta.variant)}
                      style={{ width: "100%", textAlign: "center" }}
                    >
                      {plan.cta.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Add-ons */}
        <section className="section white" aria-labelledby="addons-title">
          <div className="container section-reveal">
            <div className="section-header centered">
              <span className="overline">ADD-ONS &amp; ONE-TIME CHARGES</span>
              <h2 id="addons-title">Need something extra?</h2>
            </div>
            <div className="pricing-table-wrap">
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                  fontFamily: "var(--font-ui)",
                }}
              >
                <thead>
                  <tr>
                    <th style={headerStyle}>ADD-ON</th>
                    <th style={headerStyle}>PRICE</th>
                  </tr>
                </thead>
                <tbody>
                  {ADDONS.map((a) => (
                    <tr key={a.name}>
                      <td style={cellBase}>{a.name}</td>
                      <td style={cellBase}>{a.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p
              style={{
                marginTop: 16,
                fontSize: 12,
                color: "var(--text-muted)",
                textAlign: "center",
              }}
            >
              All prices are excluding state and central tax.
            </p>
          </div>
        </section>

        {/* Comparison table */}
        <section className="section" aria-labelledby="compare-title">
          <div className="container section-reveal">
            <div className="section-header centered">
              <h2 id="compare-title">Everything side by side.</h2>
            </div>

            <div className="pricing-table-wrap">
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                  fontFamily: "var(--font-ui)",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...headerStyle, width: "40%" }}>FEATURE</th>
                    <th style={headerCenter}>STARTER</th>
                    <th style={headerGrowth}>GROWTH</th>
                    <th style={headerCenter}>ENTERPRISE</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((section) => (
                    <Fragment key={section.title}>
                      <tr key={`${section.title}-h`}>
                        <td colSpan={4} style={sectionLabelCell}>
                          {section.title}
                        </td>
                      </tr>
                      {section.rows.map((row) => (
                        <tr key={`${section.title}-${row.label}`}>
                          <td style={cellBase}>{row.label}</td>
                          {renderCell(row.values[0], false)}
                          {renderCell(row.values[1], true)}
                          {renderCell(row.values[2], false)}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section white" aria-labelledby="faq-title">
          <div className="container section-reveal">
            <div className="section-header centered">
              <span className="overline">COMMON QUESTIONS</span>
              <h2 id="faq-title">Everything you need to know.</h2>
            </div>

            <div className="faq-list">
              {FAQS.map((faq, i) => (
                <FaqItem
                  key={faq.q}
                  question={faq.q}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <p>{faq.a}</p>
                </FaqItem>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Band */}
        <section
          className="cta-band grain"
          id="cta"
          aria-labelledby="pricing-cta-title"
        >
          <div className="container">
            <div className="cta-band__content section-reveal">
              <span className="overline overline--light">TALK TO OUR TEAM</span>
              <h2 id="pricing-cta-title">
                Not sure which plan?
                <br />
                <em className="accent-italic">Let&apos;s figure it out</em>{" "}
                together.
              </h2>
              <p>Schedule a 15-minute call with our team.</p>
              <div className="btn-row">
                <Link
                  to="/signup"
                  state={{ fromApp: true }}
                  className="btn btn-accent btn-lg"
                >
                  Get started
                </Link>
                <Link
                  to="/contact"
                  state={{ fromApp: true }}
                  className="btn btn-ghost-dark btn-lg"
                >
                  Talk to Sales team &rarr;
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
